import { readCopyFlag } from '@/lib/copy-search';

/**
 * Ce que l'URL de l'éditeur de protocole porte (refonte modulaire §4, §7.1 ;
 * noyau.md §4.9, N47 ; issue #154).
 *
 * Three parameters, read by the two routes that mount the editor - the empty
 * one and the one of an existing protocol - so the reading is written once,
 * pure and testable: `vitest.config.ts` excludes `src/routes/**` from the
 * coverage report, and a `validateSearch` written inline in two files would be
 * two spellings of one rule (the argument of `experience-search.ts`).
 *
 *   - **`?version=`** - « Voir », the read-only opening of §4;
 *   - **`?vue=`** - the position of the switch of §3.5 of the analyste, « Conduite
 *     de l'entretien », « Analyste » or, since #177, « Rangement ». It travels in
 *     the URL at the régime of the experience editor's `?tab=` (N47), which is
 *     what lets « Ouvrir le protocole » in an experience's save box land straight
 *     on the section (§3.6);
 *   - **`?copie`** - the flag of §4.2, consumed once the editor has opened.
 *
 * ## TWO values are spelled: `lecture` and `rangement`
 *
 * `lecture` NAMES THE SECTION ANALYSTE since #196, and the value stays what it was
 * when the view was « Lecture pour l'analyste » (#154): « la position voyage dans
 * l'URL comme elle » (analyste.md §3.5, N47), so no address pasted in a thread
 * breaks, and the typed links of the experiences and of the parcours compile as
 * they were written.
 *
 * « Conduite de l'entretien » is the view by default, so it is the ABSENCE of
 * the parameter and never `?vue=conduite`: a default that had two spellings
 * would put two addresses on one screen, and a link pasted into a thread
 * would not compare equal to the one somebody else pasted. Anything but
 * `lecture` and `rangement` degrades to the conduite (« une adresse inconnue retombe sur la
 * conduite », N47), the rule every filtered screen of this repo holds: a typo
 * in a pasted link opens a screen, never a blank one.
 *
 * ## The keys are OMITTED, never `undefined`
 *
 * `exactOptionalPropertyTypes` makes `{ vue: undefined }` and `{}` two
 * different types, and TanStack writes every key it is handed into the address
 * bar - a present key holding `undefined` would put `?vue=` on the URL of every
 * editor opened on the conduite.
 */

/** The three positions of the switch (analyste.md §3.5, scenario-succes.md §4): the steps, the section Analyste, the rangement. */
export type ProtocolEditorView = 'conduite' | 'lecture' | 'rangement';

/** What `?vue=` carries when it carries anything - the two values that are not the default. */
export type ProtocolViewSearch = { vue?: 'lecture' | 'rangement' };

export type ProtocolEditorSearch = ProtocolViewSearch & {
  version?: string;
  /** The `?copie` of §4.2 - present and true, or absent (`copy-search.ts`). */
  copie?: true;
};

export function readProtocolEditorSearch(search: Record<string, unknown>): ProtocolEditorSearch {
  const parsed: ProtocolEditorSearch = {};
  const version = search.version;
  if (typeof version === 'string' && version !== '') parsed.version = version;
  if (search.vue === 'lecture' || search.vue === 'rangement') parsed.vue = search.vue;
  const copie = readCopyFlag(search.copie);
  if (copie !== undefined) parsed.copie = copie;
  return parsed;
}

/** The view an address names: the section Analyste on `?vue=lecture`, the rangement on `?vue=rangement`, the conduite on anything else. */
export function protocolViewOf(search: ProtocolViewSearch): ProtocolEditorView {
  return search.vue === 'lecture' || search.vue === 'rangement' ? search.vue : 'conduite';
}

/**
 * The search a view is written as - spread into every navigation of the two
 * routes, so « Voir », « Revenir au brouillon » and the switch all keep the
 * view somebody is reading.
 */
export function viewSearch(view: ProtocolEditorView): ProtocolViewSearch {
  return view === 'conduite' ? {} : { vue: view };
}
